import { Observable, Subject, Scheduler, BehaviorSubject } from "rxjs";
import { Injectable } from "@angular/core";
import { Http, Headers, Request, ResponseContentType, RequestOptions, RequestMethod, Response } from "@angular/http";

import * as CryptoJS from "crypto-js";
import * as moment from "moment";

import { UserSelf } from "app/models/userSelf";
import { Client } from "app/models/client";
import { User } from "app/models/user";

const authenticatedUserKey = "authenticatedUser";

@Injectable()
export class AuthenticationService {

  private readonly _authenticatedUserSubject: BehaviorSubject<UserSelf>;

  constructor(
    private _http: Http
  ) {
    let authenticatedUserJson = localStorage.getItem(authenticatedUserKey);
    this._authenticatedUserSubject = new BehaviorSubject(authenticatedUserJson
      ? Object.assign(Object.create(UserSelf.prototype), JSON.parse(authenticatedUserJson))
      : null);

    // сохранение данных аутентифцированного пользователя в localStorage
    this._authenticatedUserSubject
      .subscribe(user => {

        if (user)
          localStorage.setItem(authenticatedUserKey, JSON.stringify(user));
        else
          localStorage.removeItem(authenticatedUserKey);
      });

    // обновление токена доступа
    this._authenticatedUserSubject
      .observeOn(Scheduler.async)
      .debounce(user => {

        return Observable.of(null).delay(
          Math.max(
            0,
            (user && user.accessData ? moment.utc(user.accessData.accessTokenValidTo).diff(moment.utc(), "milliseconds") : 0) / 2
          )
        );
      })
      .filter(user => user != null && user != undefined)
      .subscribe(user => {

        this._http.post("/api/user/token", null, {
          headers: new Headers({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `x-user-service ${user.accessData.serviceToken}`
          }),
          responseType: ResponseContentType.Json,
        })
          // повторяем попытку при ошибке
          .retryWhen(errors => errors
            .switchMap(error => {
              // если ошибка - это http-ответ с кодами 4xx или 5xx, то повторять не имеет смысла - выходим
              if (error instanceof Response && error.status >= 400 && error.status < 600)
                return Observable.throw(error);
              return Observable.of(error);
            })
            // каждая попытка отложена на 5 секунд
            .delay(5000))
          .subscribe(response => {
            if (response.ok) {
              let accessData = this.getAccessData(response);
              user.accessData = accessData;
              let rolePermission = this.getRoleAndPermissions(accessData.accessToken);
              user.role = rolePermission[0];
              user.permissions = rolePermission[1];
              if (this._authenticatedUserSubject.value == user) {
                // сообщаем об изменении данных пользователя
                // т.к. мы находимся внутри подписчика, то Observable, на который мы подписались должен иметь observeOn(Scheduler.async), иначе в данной строчке будет переполнение стека
                this._authenticatedUserSubject.next(user);
              }
            }
          }, error => {
            if (this._authenticatedUserSubject.value == user) {
              // обновить токен не удалось, следовательно, дальнейшая аутентификация пользователя невозможна
              this._authenticatedUserSubject.next(null);
            }
          });
      });
  }

  signIn(username: string, password: string): Observable<UserSelf> {

    let passwordHash = CryptoJS.SHA256(password);
    let salt = CryptoJS.lib.WordArray.random(16);
    let hashedPassword = CryptoJS.SHA256(passwordHash.concat(salt));

    let signinRequest = this._http.post(
      '/api/user/signin',
      {
        "UserName": username,
        "HashedPassword": hashedPassword.toString(CryptoJS.enc.Base64),
        "Salt": salt.toString(CryptoJS.enc.Base64)
      }, {
      headers: new Headers({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }),
      responseType: ResponseContentType.Json,
    });

    return signinRequest
      .catch(err => {
        if (err instanceof Response) {
          let response = err as Response;
          return Observable.throw(this.resolveSigninError(response));
        }
        return Observable.throw("Unknown" as AuthenticationService.SigninError);
      })
      .switchMap(response => {
        if (!response.ok)
          return Observable.throw(this.resolveSigninError(response));

        let accessData = this.getAccessData(response);

        let getSelfRequest = this._http.get("/api/user/self", {
          headers: new Headers({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `x-user ${accessData.accessToken}`
          }),
          responseType: ResponseContentType.Json,
        });
        return getSelfRequest
          .catch(err => Observable.throw("Unknown" as AuthenticationService.SigninError))
          .switchMap(response => {
            if (!response.ok)
              return Observable.throw("Unknown" as AuthenticationService.SigninError);

            let body = response.json()
            let user = new UserSelf();
            user.id = body.Id;
            user.name = body.Username;
            user.email = body.Email;
            let rolePermission = this.getRoleAndPermissions(accessData.accessToken);
            user.role = rolePermission[0];
            user.permissions = rolePermission[1];
            user.client = new Client();
            user.client.callConfirmation = body.Client.CallConfirmation;
            user.client.canAccessSurveys = body.Client.CanAccessSurveys;
            user.client.canAccessPhotos = body.Client.CanAccessPhotos;
            user.client.canAccessReports = body.Client.CanAccessReports;
            user.client.name = body.Client.Name;
            user.client.id = body.Client.Id;
            user.client.email = body.Client.Email;

            user.accessData = accessData;
            return Observable.of(user);
          })
          .do(
            user => this._authenticatedUserSubject.next(user),
            err => this._authenticatedUserSubject.next(null))
      });
  }

  replaceClientSettings() {
    let user = this._authenticatedUserSubject.value;
    if (user) {
      let getSelfRequest = this._http.get("/api/user/self", {
        headers: new Headers({
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `x-user ${user.accessData.accessToken}`
        }),
        responseType: ResponseContentType.Json,
      });
      getSelfRequest
        .catch(err => Observable.throw("Unknown" as AuthenticationService.SigninError))
        .switchMap(response => {
          if (!response.ok)
            return Observable.throw("Unknown" as AuthenticationService.SigninError);

          let body = response.json()
          user.client.callConfirmation = body.Client.CallConfirmation;
          user.client.canAccessSurveys = body.Client.CanAccessSurveys;
          user.client.canAccessPhotos = body.Client.CanAccessPhotos;
          user.client.canAccessReports = body.Client.CanAccessReports;
          user.client.name = body.Client.Name;
          user.client.id = body.Client.Id;
          user.client.email = body.Client.Email;

          return Observable.of(user);
        })
        .do(
          user => this._authenticatedUserSubject.next(user),
          err => this._authenticatedUserSubject.next(null))
        .subscribe();
    }
  }

  signOut() {
    this._authenticatedUserSubject.next(null);
  }

  get authenticatedUser(): UserSelf {
    return this._authenticatedUserSubject.value;
  }

  get authenticatedUserChange(): Observable<UserSelf> {
    return this._authenticatedUserSubject.skip(1);
  }

  private resolveSigninError(response: Response): AuthenticationService.SigninError {
    let body: any;
    let errorCode: number;
    switch (response.status) {
      case 401:
        body = null;
        try {
          body = response.json();
        } finally { }
        errorCode = body && body.ErrorCode ? body.ErrorCode : null;
        switch (errorCode) {
          case 1:
            return "InvalidUserNameOrPassword";
          case 2:
            return "SaltAlreadyUsed";
        }
        break;
      case 403:
        body = null;
        try {
          body = response.json();
        } finally { }
        errorCode = body && body.ErrorCode ? body.ErrorCode : null;
        switch (errorCode) {
          case 3:
            return "UserNotActivated";
          case 7:
            return "UserIsLocked";
          case 9:
            return "ClientIsLocked";
          case 10:
            return "PasswordResetRequired";
        }
        break;

    }
    return "Unknown";
  }

  private getRoleAndPermissions(accessToken: string): [User.Role, User.Permissions] {
    let decodedJwtData: any = this.getDecodedJwtData(accessToken);
    let rolesCpaStrings: string[] = Array.isArray(decodedJwtData.role) ? decodedJwtData.role : [decodedJwtData.role];
    let roleCpa: AuthenticationService.RoleCPA;

    rolesCpaStrings.forEach(role => {
      roleCpa |= AuthenticationService.RoleCPA[role];
    });

    let userRole: User.Role;
    let permission: User.Permissions = User.Permissions.None;

    if ((roleCpa & AuthenticationService.RoleCPA.All) == AuthenticationService.RoleCPA.All) {
      userRole = User.Role.Administrator;
      permission = User.Permissions.All;
    }
    else {
      userRole = User.Role.Manager;

      if ((roleCpa & AuthenticationService.RoleCPA.Read) == AuthenticationService.RoleCPA.Read) {
        permission |= User.Permissions.Read;
      }

      if ((roleCpa & AuthenticationService.RoleCPA.Write) == AuthenticationService.RoleCPA.Write) {
        permission |= User.Permissions.Write;
      }
    }



    return [userRole, permission];
  }

  private getDecodedJwtData(accessToken: string) {
    return JSON.parse(this.base64UrlDecode(accessToken.split('.')[1]));
  }

  // atob не может в utf-8, поэтому пришлось делать так
  private base64UrlDecode(base64Url) {
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    let jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return jsonPayload
}

  private getAccessData(response: Response) {
    let body = response.json();
    let now = moment.utc();
    return new User.AccessData(
      body.Access.Token,
      now.clone().add(body.Access.ExpiresIn, 'milliseconds').toDate(),
      body.Service.Token,
      now.clone().add(body.Service.ExpiresIn, 'milliseconds').toDate()
    );
  }
}

export namespace AuthenticationService {
  export type SigninError = ("Unknown" | "InvalidUserNameOrPassword" | "SaltAlreadyUsed" | "UserNotActivated" | "UserIsLocked" | "ClientIsLocked" | "PasswordResetRequired");

  export enum RoleCPA {
    None = 0,
    Read = 1,
    Write = 1 << 1,
    UserManagement = 1 << 2,
    All = ~None
  }
}



// WEBPACK FOOTER //
// /app/SPA/src/app/services/authentication.service.ts